<template>
  <panel-layout
    id="form-panel"
    :loading="loading"
    :show-footer="false"
  >
    <template v-if="customerForm" #actions-right>
      <div class="flex items-center space-x-1">
        <o-tooltip v-if="!isFormStateLocked" :text="$t('form.fillInForm')">
          <o-panel-link
            tag="o-button"
            flat
            size="sm"
            :path="`/customer/form/${customerForm.idCustomerForm}`"
            icon="edit"
          >
            {{ $t('general.edit') }}
          </o-panel-link>
        </o-tooltip>

        <o-tooltip v-if="!isRequestStateRequested && !isRequestStateScheduled" :text="$t('general.print')">
          <o-button flat size="sm" icon="print" @click="handleFormPrint()" />
        </o-tooltip>

        <o-tooltip v-if="customerForm.reservationCustomer" :text="$t('general.reservation')" class="h-8">
          <o-panel-link
            v-if="customerForm.reservationCustomer"
            tag="o-button"
            flat
            :path="`/appointment/attendee/${customerForm.reservationCustomer.idReservationCustomer}`"
            icon="reservation"
          />
        </o-tooltip>

        <o-tooltip v-if="customerForm.reservation" :text="$t('general.reservation')" class="h-8">
          <o-panel-link
            v-if="customerForm.reservation"
            tag="o-button"
            flat
            :path="`/appointment/${customerForm.reservation.idReservation}?date=${customerForm.reservation.date}`"
            icon="reservation"
          />
        </o-tooltip>
      </div>
    </template>

    <template #heading>
      <div v-if="customerForm" class="flex items-center space-x-2">
        <div>
          <h2 class="text-lg md:text-2xl tracking-tighter font-bold text-gray-800/85 leading-none dark:text-gray-100">
            {{ customerForm.form.title }}
          </h2>
          <div class="mt-2 mb-4 flex flex-wrap md:flex-no-wrap items-center text-gray-700 text-sm dark:text-gray-200">
            <o-panel-link :path="`/customer/panel?id=${customerForm.customer.idCustomer}`" class="flex items-center mr-4 text-sm capitalize">
              <o-icon icon="customer" :size="14" class="mr-2" />
              <p class="leading-none">
                {{ customerForm.customer.displayName }}
              </p>
            </o-panel-link>
            <div class="flex items-center text-sm">
              <o-icon icon="calendar" :size="13" class="block mr-2 pb-2px" />
              <p class="leading-none">
                {{ customerForm.createdLabel }}
              </p>
            </div>
          </div>
          <form-status :customerForm="customerForm" />
        </div>
      </div>
    </template>

    <div class="h-full">
      <o-scroll>
        <div v-if="customerForm" ref="content" class="h-full p-5">
          <div class="hidden print:block mb-12">
            <h2 class="text-lg md:text-2xl tracking-tighter font-bold text-gray-800/85 leading-none dark:text-gray-100">
              {{ customerForm.form.title }}
            </h2>
            <div class="mt-2 mb-4 flex flex-wrap md:flex-no-wrap items-center text-gray-700 text-sm dark:text-gray-200">
              <o-panel-link :path="`/customer/panel?id=${customerForm.customer.idCustomer}`" class="flex items-center mr-4 text-sm capitalize">
                <o-icon icon="customer" :size="14" class="mr-2" />
                <p class="leading-none">
                  {{ customerForm.customer.displayName }}
                </p>
              </o-panel-link>
              <div class="flex items-center text-sm">
                <o-icon icon="calendar" :size="13" class="block mr-2 pb-2px" />
                <p class="leading-none">
                  {{ customerForm.createdLabel }}
                </p>
              </div>
            </div>
            <form-status :customerForm="customerForm" />
          </div>

          <form-controller :customerForm="customerForm" class="pb-12 block">
            <template #field="{ field }">
              <div class="mb-2 text-sm">
                <p class="text-gray-600 mb-1 dark:text-gray-300">
                  {{ field.name }}
                </p>
                <template v-if="field.isDataTypeAnnotation">
                  <div class="bg-white p-2 border rounded dark:bg-gray-900" v-html="field.valueLabel" />
                </template>
                <p v-else class="text-sm break-words">
                  <template v-if="field.valueLabel && field.valueLabel.length">
                    <span v-html="field.valueLabel" />
                  </template>
                  <template v-else>
                    <span class="text-gray-300 dark:text-gray-600">-</span>
                  </template>
                </p>
              </div>
            </template>
          </form-controller>
        </div>
      </o-scroll>
    </div>

    <o-dialog
      ref="confirmDialog"
      :title="$t('general.unsavedChanges')"
      :info="$t('general.unsavedChangesInfo')"
      :options="[
        { text: $t('general.back'), type: 'cancel' },
        { text: $t('general.closeWithoutSaving'), type: 'confirm', variant: undefined, flat: true, outline: true, callback: () => cancel(true) },
        { text: $t('general.saveAndClose'), type: 'confirm', response: 'save', callback: () => save(true) }
      ]"
      :portal="panel.portal"
    />
  </panel-layout>
</template>
<script setup lang="ts">
// @ts-ignore
import PanelLayout from '@/components/panel/PanelLayout.vue'
// @ts-ignore
import FormController from '@/components/customerForm/edit/FormController.vue'
// @ts-ignore
import FormStatus from '@/components/customerForm/elements/Status.vue'

// @ts-ignore
import SignaturePreview from '@/components/customerForm/elements/SignaturePreview.vue'

// @ts-ignore
import {
  OAlert,
  OCustomFields,
  ODescriptionDetails,
  OFieldGroup,
} from '@/components/elements'

import { usePanel } from '~/composables/usePanel'

const props = defineProps<{ id?: string }>()

const { $api, $print } = useNuxtApp()

const panel = usePanel()

const content = ref<any>()

const loading = ref(false)
const customerForm = ref<any>(null)

const isRequestStateNone = computed(() => customerForm.value.isRequestStateNone)
const isRequestStateRequested = computed(() => customerForm.value.isRequestStateRequested)
const isRequestStateCompleted = computed(() => customerForm.value.isRequestStateCompleted)
const isRequestStateScheduled = computed(() => customerForm.value.isRequestStateScheduled)
const isRequestStateUpdateRequested = computed(() => customerForm.value.isRequestStateUpdateRequested)
const isFormStateLocked = computed(() => customerForm.value.isFormStateLocked)
const isFormStateReopened = computed(() => customerForm.value.isFormStateReopened)

const handleFormPrint = () => {
  nextTick(() => {
    $print(content.value.innerHTML)
  })
}

const init = async (id?: string) => {
  loading.value = true

  try {
    if (id) customerForm.value = (await $api.customerForm.get(id)).data
  } catch (e) {
    console.log(e)
  }

  loading.value = false
}

watch(() => props.id, (id) => init(id), { immediate: true })
</script>
